import type { FeedEntry } from '~/services/layout/index.ts';

export const QUEUE_PIPE_PREFIX = 'pipesv2://queue-action/';

export const getQueueItemId = (item: FeedEntry): string =>
  String(item?.extensions?.queueItemId || item?.id);

export const findIndexById = (state: FeedEntry[], id: string): number => {
  if (!id || !Array.isArray(state)) return -1;

  const idx = state.findIndex(
    (item) => String(item.extensions?.queueItemId) === String(id)
  );

  return idx !== -1
    ? idx
    : state.findIndex((item) => String(item.id) === String(id));
};

export function reorderByIds<T>(
  items: T[],
  orderedIds: string[],
  idSelector: (item: T) => string | string[]
): T[] {
  if (!items || items.length === 0) return [];
  if (!orderedIds || orderedIds.length === 0) return [...items];

  const itemMap = new Map<string, T>();

  items.forEach((item) => {
    const rawId = idSelector(item);
    const keys = Array.isArray(rawId) ? rawId : [rawId];

    keys.forEach((key) => {
      if (key !== undefined && key !== null && key !== '') {
        itemMap.set(String(key), item);
      }
    });
  });

  const reorderedSubset: T[] = [];
  const orderedIdsSet = new Set<string>();

  orderedIds.forEach((id) => {
    const strId = String(id);
    const item = itemMap.get(strId);

    if (item && !reorderedSubset.includes(item)) {
      reorderedSubset.push(item);
      const rawId = idSelector(item);
      const keys = Array.isArray(rawId) ? rawId : [rawId];

      keys.forEach((k) => {
        if (k !== undefined && k !== null && k !== '') {
          orderedIdsSet.add(String(k));
        }
      });
    }
  });

  if (reorderedSubset.length === 0) return [...items];

  let subsetIdx = 0;
  const result: T[] = [];

  items.forEach((item) => {
    const rawId = idSelector(item);
    const keys = Array.isArray(rawId) ? rawId : [rawId];

    const isMatched = keys.some(
      (k) =>
        k !== undefined &&
        k !== null &&
        k !== '' &&
        orderedIdsSet.has(String(k))
    );

    if (isMatched) {
      if (subsetIdx < reorderedSubset.length) {
        result.push(reorderedSubset[subsetIdx++]);
      }
    } else {
      result.push(item);
    }
  });

  while (subsetIdx < reorderedSubset.length) {
    result.push(reorderedSubset[subsetIdx++]);
  }

  return result;
}

export const generateQueueItemId = (): string =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export const isQueuePipeUrl = (url?: string): boolean =>
  typeof url === 'string' && url.startsWith(QUEUE_PIPE_PREFIX);

export const resolvePlayFeedUrl = (
  providedUrl: string | undefined,
  path: 'play-next' | 'play-previous',
  queueItemId: string
): string => {
  if (providedUrl && !isQueuePipeUrl(providedUrl)) {
    return providedUrl;
  }

  return `${QUEUE_PIPE_PREFIX}${path}?queueItemId=${queueItemId}`;
};

export const matchesQueuedItem = (
  item: FeedEntry,
  entry: FeedEntry
): boolean => {
  if (!item || !entry) return false;

  const entryQueueItemId = entry.extensions?.queueItemId;
  const itemQueueItemId = item.extensions?.queueItemId;

  if (entryQueueItemId && itemQueueItemId) {
    return String(itemQueueItemId) === String(entryQueueItemId);
  }

  const entryContentId = entry.extensions?.contentId;
  const itemContentId = item.extensions?.contentId;

  if (entryContentId && itemContentId) {
    return String(itemContentId) === String(entryContentId);
  }

  return String(item.id) === String(entry.id);
};

export const injectQueueExtensions = (entry: FeedEntry): FeedEntry => {
  const contentId = String(entry.extensions?.contentId || entry.id);
  const queueItemId =
    (entry.extensions?.queueItemId as string | undefined) ||
    (entry.extensions?.queue_instance_id as string | undefined) ||
    generateQueueItemId();

  return {
    ...entry,
    id: entry.id,
    extensions: {
      ...entry.extensions,
      contentId,
      queueItemId,
      queue_instance_id: queueItemId,
      play_next_feed_url: resolvePlayFeedUrl(
        entry.extensions?.play_next_feed_url,
        'play-next',
        queueItemId
      ),
      play_previous_feed_url: resolvePlayFeedUrl(
        entry.extensions?.play_previous_feed_url,
        'play-previous',
        queueItemId
      ),
    },
  };
};
